import { supabase } from './supabaseClient';

export type EventType =
  | 'page_view'
  | 'whatsapp_click'
  | 'phone_click'
  | 'preventivo_cta'
  | 'form_submit'
  | 'nav_click';

export interface TrackingEvent {
  id: string;
  type: EventType;
  label: string;
  source: string;
  timestamp: number;
  date: string;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export function trackEvent(type: EventType, label: string, source: string): void {
  const event: TrackingEvent = {
    id: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    type,
    label,
    source,
    timestamp: Date.now(),
    date: new Date().toISOString().slice(0, 10),
  };

  // Save to Supabase (fire-and-forget, non blocking)
  supabase
    .from('analytics_events')
    .insert({
      id: event.id,
      type: event.type,
      label: event.label,
      source: event.source,
      timestamp: event.timestamp,
      date: event.date,
    })
    .then(({ error }) => {
      if (error) {
        // fallback: save locally if Supabase fails
        try {
          const STORAGE_KEY = 'pc_analytics_v1';
          const raw = localStorage.getItem(STORAGE_KEY);
          const events: TrackingEvent[] = raw ? (JSON.parse(raw) as TrackingEvent[]) : [];
          events.unshift(event);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(0, 200)));
        } catch {
          // ignore
        }
      }
    });

  // Google Analytics
  try {
    if (window.gtag) {
      window.gtag('event', type, {
        event_category: source,
        event_label: label,
      });
    }
  } catch {
    // gtag unavailable
  }
}

export async function getEventsFromSupabase(): Promise<TrackingEvent[]> {
  const { data, error } = await supabase
    .from('analytics_events')
    .select('id, type, label, source, timestamp, date')
    .order('timestamp', { ascending: false })
    .limit(500);

  if (error || !data) return [];
  return data as TrackingEvent[];
}

export async function deleteAllEventsFromSupabase(): Promise<void> {
  await supabase
    .from('analytics_events')
    .delete()
    .gte('timestamp', 0);
}
